import React, { useImperativeHandle, forwardRef, useEffect, useContext } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, CircularProgress, IconButton, Tooltip, Grid, Divider } from '@mui/material';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';
import { useFormContext, useForm } from 'react-hook-form';
import { ObjectProperty } from 'views/backoffice/CustomObjects/types';
import { Close, Save } from '@mui/icons-material';
import { RenderInput } from 'ui-component/RecordView/components/RenderInput';
import { IRecordField } from 'ui-component/records/types';
import { emptyRequiredFieldsErrorMessage } from 'views/DocumentViewer/utils';
import { PanelFormsContext } from 'contexts/panelFormsContext';
import { pinWay } from 'ui-component/RecordView/PropertiesPanel/utils';

interface ObjectPanelEditFormProps {
    objectProperties: (ObjectProperty & { value: string; objectName: string })[];
    setObjectProperties?: (value: (ObjectProperty & { value: string; objectName: string })[]) => void;
    handleUpdateObjectViewer?: (values: { [key: string]: string }, modalTitle: string) => void;
    handleClosePanelViewer?: (values: { [key: string]: string }, modalTitle: string) => void;
    handleUpdateObjectGrid?: (newProperties: (ObjectProperty & { value: string; objectName: string })[], modalTitle: string) => void;
    handleClosePanelGrid?: (newProperties: (ObjectProperty & { value: string; objectName: string })[], modalTitle: string) => void;
    isRecordViewer?: boolean;
    updateLoading?: boolean;
    objectEditingRestricted?: boolean;
    showFieldMenu?: boolean;
    rightHandPanelWidth?: number;
    PanelTitle?: (rightHandPanelWidth: number) => React.ReactNode;
}

const ObjectPanelEditForm = forwardRef(
    (
        {
            objectProperties,
            setObjectProperties,
            handleUpdateObjectViewer,
            handleClosePanelViewer,
            handleUpdateObjectGrid,
            handleClosePanelGrid,
            isRecordViewer = false,
            updateLoading = false,
            objectEditingRestricted = false,
            showFieldMenu = true,
            rightHandPanelWidth,
            PanelTitle
        }: ObjectPanelEditFormProps,
        ref
    ) => {
        const theme = useTheme();
        const { setCustomObjectFieldsOrder: onReorderFields, enableDragAndDrop, pinnedFields } = useContext(PanelFormsContext);
        const defaultValues: { [key: string]: string } = {};
        objectProperties.forEach((item) => {
            defaultValues[item.name] = item.value;
        });

        const formContext = useFormContext();
        const formMethods = useForm({
            mode: 'onChange',
            defaultValues
        });

        const methods = isRecordViewer ? formContext : formMethods;

        useImperativeHandle(ref, () => ({
            getValues: () => methods.getValues(),
            validateObjectChanges: () => validateObjectChanges()
        }));

        useEffect(() => {
            objectProperties.forEach((item) => {
                methods?.setValue(item.name, item.value);
            });
        }, [objectProperties, methods]);

        const validateRequiredFields = () => {
            const values = methods.getValues();
            const requiredFields = objectProperties.filter((item) => item.isRequired);
            let hasErrors = false;

            requiredFields.forEach((item) => {
                const itemValue = values[item.name];

                if (
                    itemValue === undefined ||
                    itemValue === null ||
                    itemValue === '' ||
                    itemValue === '0' ||
                    (Array.isArray(itemValue) && itemValue.length === 0)
                ) {
                    methods.setError(item.name, {
                        type: 'manual',
                        message: emptyRequiredFieldsErrorMessage(item.dataType || '')
                    });
                    hasErrors = true;
                }
            });

            return hasErrors;
        };

        const getObjectValues = () => {
            const values = methods.getValues();
            const objectValues = objectProperties.reduce((acc, item) => {
                acc[item.name] = values[item.name];
                return acc;
            }, {} as { [key: string]: string });
            return objectValues;
        };

        const validateObjectChanges = () => {
            if (updateLoading) return;
            const hasErrors = validateRequiredFields();
            if (hasErrors) {
                return;
            }

            const values = getObjectValues();

            if (isRecordViewer) {
                handleUpdateObjectViewer?.(values, 'Do you want to save this changes?');
            } else {
                const newObjectProperties = objectProperties.map((item: ObjectProperty & { value: string; objectName: string }) => ({
                    ...item,
                    value: values[item.name]
                }));
                setObjectProperties?.(newObjectProperties);
                handleUpdateObjectGrid?.(newObjectProperties, 'Do you want to save this changes?');
            }
        };

        const handleDiscardChanges = () => {
            const values = methods.getValues();
            if (isRecordViewer) {
                handleClosePanelViewer?.(values, 'Are you sure you want to discard your changes?');
            } else {
                const newObjectProperties = objectProperties.map((item: ObjectProperty & { value: string; objectName: string }) => ({
                    ...item,
                    value: values[item.name]
                }));
                setObjectProperties?.(newObjectProperties);
                handleClosePanelGrid?.(newObjectProperties, 'Are you sure you want to discard your changes?');
            }
        };

        const pinnedUpFieldEntries = pinnedFields
            .filter((fieldName) => fieldName.includes(pinWay.up))
            .map((fieldName) =>
                objectProperties.find((item) => {
                    const propertyFieldName = `${item.objectName};;${item.name}`;
                    return propertyFieldName === fieldName.replace(pinWay.up, '');
                })
            )
            .filter(Boolean) as (ObjectProperty & { value: string; objectName: string })[];

        const unpinnedFieldEntries = objectProperties.filter((item) => {
            const fieldId = `${item.objectName};;${item.name}`;
            return !pinnedFields.some(
                (pinnedField) =>
                    pinnedField.includes(fieldId) ||
                    pinnedField.includes(fieldId + pinWay.up) ||
                    pinnedField.includes(fieldId + pinWay.down)
            );
        });

        const pinnedDownFieldEntries = pinnedFields
            .filter((fieldName) => fieldName.includes(pinWay.down))
            .map((fieldName) =>
                objectProperties.find((item) => {
                    const propertyFieldName = `${item.objectName};;${item.name}`;
                    return propertyFieldName === fieldName.replace(pinWay.down, '');
                })
            )
            .filter(Boolean) as (ObjectProperty & { value: string; objectName: string })[];

        const handleDragEnd = (result: DropResult) => {
            if (!result.destination || !onReorderFields) return;
            if (result.destination.index === result.source.index) return;

            const [reorderedItem] = unpinnedFieldEntries.splice(result.source.index, 1);
            unpinnedFieldEntries.splice(result.destination.index, 0, reorderedItem);

            onReorderFields(unpinnedFieldEntries.map((item) => `${item.objectName};;${item.name}`));
        };

        if (!enableDragAndDrop) {
            return (
                <Box height="100%" width="100%" display="flex">
                    <Box width="100%" height="100%">
                        {PanelTitle ? PanelTitle(rightHandPanelWidth || 0) : null}
                        <Box
                            onSubmit={(e) => {
                                e.preventDefault();
                                validateObjectChanges();
                            }}
                            component="form"
                            sx={{
                                width: '100%',
                                height: '100%',
                                overflowY: 'auto'
                            }}
                        >
                            <Grid
                                container
                                data-testid="object-details-list-edit"
                                sx={{
                                    height: '100%',
                                    flexDirection: 'column',
                                    position: 'relative'
                                }}
                            >
                                {pinnedUpFieldEntries.length > 0 && (
                                    <Grid
                                        item
                                        sx={{
                                            pl: '0.8745rem',
                                            pr: '0.5rem',
                                            width: '100%',
                                            maxWidth: '100%',
                                            position: 'sticky',
                                            top: 0,
                                            backgroundColor: 'background.paper',
                                            zIndex: 1
                                        }}
                                    >
                                        {pinnedUpFieldEntries.map((item) => (
                                            <RenderInput
                                                key={item.name}
                                                name={item.name}
                                                control={methods.control}
                                                fieldData={item as unknown as IRecordField}
                                                defaultValue={item.value}
                                                errors={methods.formState.errors}
                                                options={[]}
                                                isCustomList={false}
                                                disabled={objectEditingRestricted}
                                                isRequired={item.isRequired}
                                                setValue={methods.setValue}
                                                handleOpenViewObjectPanel={() => {}}
                                                resetField={methods.resetField}
                                                showFieldMenu={showFieldMenu}
                                                isDraggable={false}
                                                isObjectProperty
                                                rightHandPanelWidth={rightHandPanelWidth}
                                                objectName={item.objectName}
                                            />
                                        ))}
                                        <Divider sx={{ width: '100%', my: '0.5rem' }} />
                                    </Grid>
                                )}
                                <Grid
                                    item
                                    sx={{
                                        width: '100%',
                                        pl: '0.8745rem',
                                        pr: '0.5rem',
                                        flex: 1,
                                        overflowY: 'auto'
                                    }}
                                >
                                    {unpinnedFieldEntries.map((item) => (
                                        <RenderInput
                                            key={item.name}
                                            name={item.name}
                                            control={methods.control}
                                            fieldData={item as unknown as IRecordField}
                                            defaultValue={item.value}
                                            errors={methods.formState.errors}
                                            options={[]}
                                            isCustomList={false}
                                            disabled={objectEditingRestricted}
                                            isRequired={item.isRequired}
                                            setValue={methods.setValue}
                                            handleOpenViewObjectPanel={() => {}}
                                            resetField={methods.resetField}
                                            showFieldMenu={showFieldMenu}
                                            isDraggable={false}
                                            isObjectProperty
                                            rightHandPanelWidth={rightHandPanelWidth}
                                            objectName={item.objectName}
                                        />
                                    ))}
                                </Grid>
                                {pinnedDownFieldEntries.length > 0 && (
                                    <Grid
                                        item
                                        sx={{
                                            pl: '0.8745rem',
                                            pr: '0.5rem',
                                            width: '100%',
                                            maxWidth: '100%',
                                            position: 'sticky',
                                            bottom: 0,
                                            backgroundColor: 'background.paper',
                                            zIndex: 1
                                        }}
                                    >
                                        <Divider sx={{ width: '100%', my: '0.5rem' }} />
                                        {pinnedDownFieldEntries.map((item) => (
                                            <RenderInput
                                                key={item.name}
                                                name={item.name}
                                                control={methods.control}
                                                fieldData={item as unknown as IRecordField}
                                                defaultValue={item.value}
                                                errors={methods.formState.errors}
                                                options={[]}
                                                isCustomList={false}
                                                disabled={objectEditingRestricted}
                                                isRequired={item.isRequired}
                                                setValue={methods.setValue}
                                                handleOpenViewObjectPanel={() => {}}
                                                resetField={methods.resetField}
                                                showFieldMenu={showFieldMenu}
                                                isDraggable={false}
                                                isObjectProperty
                                                rightHandPanelWidth={rightHandPanelWidth}
                                                objectName={item.objectName}
                                            />
                                        ))}
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </Box>
                    {!isRecordViewer && (
                        <Box
                            sx={{
                                width: '3.25rem',
                                backgroundColor: '#F5F6F7',
                                height: '100%',
                                display: 'flex',
                                py: '0.75rem',
                                px: '0.3rem',
                                flexDirection: 'column',
                                justifyContent: 'space-between'
                            }}
                        >
                            <Box sx={{ paddingRight: '1rem' }}>
                                <Tooltip title="Discard Changes">
                                    <IconButton
                                        aria-label="cancel"
                                        sx={{
                                            backgroundColor: theme.palette?.primary[200],
                                            '&:hover': { backgroundColor: theme.palette?.primary[200] }
                                        }}
                                        onClick={() => handleDiscardChanges()}
                                    >
                                        <Close sx={{ color: '#666' }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Box sx={{ paddingRight: isRecordViewer ? '0' : '1rem' }}>
                                <Tooltip title="Save Changes">
                                    <IconButton
                                        aria-label="save"
                                        sx={{
                                            backgroundColor: updateLoading ? theme.palette?.primary[200] : theme.palette?.secondary.main,
                                            '&:hover': {
                                                backgroundColor: updateLoading ? theme.palette?.primary[200] : theme.palette?.secondary.main
                                            }
                                        }}
                                        onClick={() => validateObjectChanges()}
                                    >
                                        {updateLoading ? (
                                            <CircularProgress color="inherit" size={20} sx={{ color: '#666' }} />
                                        ) : (
                                            <Save sx={{ color: '#FFF' }} />
                                        )}
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    )}
                </Box>
            );
        }

        return (
            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="edit-object-panel-fields">
                    {(provided) => (
                        <Box height="100%" width="100%" display="flex">
                            <Box width="100%" height="100%">
                                {PanelTitle ? PanelTitle(rightHandPanelWidth || 0) : null}

                                <Grid
                                    data-testid="object-panel-edit-form-grid"
                                    component="form"
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    sx={{
                                        height: '100%',
                                        flexDirection: 'column',
                                        position: 'relative'
                                    }}
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        validateObjectChanges();
                                    }}
                                >
                                    {pinnedUpFieldEntries.length > 0 && (
                                        <Grid
                                            data-testid="object-panel-edit-form-pinned-up-grid"
                                            item
                                            sx={{
                                                pl: '0.8745rem',
                                                pr: '0.5rem',
                                                width: '100%',
                                                maxWidth: '100%',
                                                position: 'sticky',
                                                top: 0,
                                                backgroundColor: 'background.paper',
                                                zIndex: 1
                                            }}
                                        >
                                            {pinnedUpFieldEntries.map((item) => (
                                                <RenderInput
                                                    key={item.name}
                                                    name={item.name}
                                                    control={methods.control}
                                                    fieldData={item as unknown as IRecordField}
                                                    defaultValue={item.value}
                                                    errors={methods.formState.errors}
                                                    options={[]}
                                                    isCustomList={false}
                                                    disabled={objectEditingRestricted}
                                                    isRequired={item.isRequired}
                                                    setValue={methods.setValue}
                                                    handleOpenViewObjectPanel={() => {}}
                                                    resetField={methods.resetField}
                                                    showFieldMenu={showFieldMenu}
                                                    isDraggable={false}
                                                    isObjectProperty
                                                    rightHandPanelWidth={rightHandPanelWidth}
                                                    objectName={item.objectName}
                                                />
                                            ))}
                                            <Divider sx={{ width: '100%', my: '0.5rem' }} />
                                        </Grid>
                                    )}
                                    <Grid
                                        data-testid="object-panel-edit-form-unpinned-grid"
                                        item
                                        sx={{
                                            width: '100%',
                                            pl: '0.8745rem',
                                            pr: '0.5rem',
                                            flex: 1,
                                            overflowY: 'auto'
                                        }}
                                    >
                                        {unpinnedFieldEntries.map((item) => (
                                            <RenderInput
                                                key={item.name}
                                                name={item.name}
                                                control={methods.control}
                                                fieldData={item as unknown as IRecordField}
                                                defaultValue={item.value}
                                                errors={methods.formState.errors}
                                                options={[]}
                                                isCustomList={false}
                                                disabled={objectEditingRestricted}
                                                isRequired={item.isRequired}
                                                setValue={methods.setValue}
                                                handleOpenViewObjectPanel={() => {}}
                                                resetField={methods.resetField}
                                                showFieldMenu={showFieldMenu}
                                                isDraggable={false}
                                                isObjectProperty
                                                rightHandPanelWidth={rightHandPanelWidth}
                                                objectName={item.objectName}
                                            />
                                        ))}
                                    </Grid>
                                    {pinnedDownFieldEntries.length > 0 && (
                                        <Grid
                                            data-testid="object-panel-edit-form-pinned-down-grid"
                                            item
                                            sx={{
                                                pl: '0.8745rem',
                                                pr: '0.5rem',
                                                width: '100%',
                                                maxWidth: '100%',
                                                position: 'sticky',
                                                bottom: 0,
                                                backgroundColor: 'background.paper',
                                                zIndex: 1
                                            }}
                                        >
                                            <Divider sx={{ width: '100%', my: '0.5rem' }} />
                                            {pinnedDownFieldEntries.map((item) => (
                                                <RenderInput
                                                    key={item.name}
                                                    name={item.name}
                                                    control={methods.control}
                                                    fieldData={item as unknown as IRecordField}
                                                    defaultValue={item.value}
                                                    errors={methods.formState.errors}
                                                    options={[]}
                                                    isCustomList={false}
                                                    disabled={objectEditingRestricted}
                                                    isRequired={item.isRequired}
                                                    setValue={methods.setValue}
                                                    handleOpenViewObjectPanel={() => {}}
                                                    resetField={methods.resetField}
                                                    showFieldMenu={showFieldMenu}
                                                    isDraggable={false}
                                                    isObjectProperty
                                                    rightHandPanelWidth={rightHandPanelWidth}
                                                    objectName={item.objectName}
                                                />
                                            ))}
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        </Box>
                    )}
                </Droppable>
            </DragDropContext>
        );
    }
);

export default ObjectPanelEditForm;
